import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useMember } from "@/pages/Member";
import { Card, Stat } from "@/components/member/MemberOverview";

const Rows = ({ items, empty, render, testid }) => (
  <div className="divide-y divide-slate-100 text-sm" data-testid={testid}>{items.length ? items.map(render) : <p className="py-2 text-slate-500">{empty}</p>}</div>
);

const Withdraw = ({ wallet, reload, call }) => {
  const [f, setF] = useState({ amount: "", account_holder: "", account_number: "", ifsc: "" });
  if (!wallet.settings.payouts_enabled)
    return <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="withdraw-disabled">Commission payouts are disabled until BMI SP GROUP completes its legal and compliance review. Your eligible commission stays recorded in your wallet.</p>;
  const submit = async () => {
    try {
      await call("post", "/mlm/me/withdrawals", { ...f, amount: Math.round(Number(f.amount) * 100) });
      toast.success("Withdrawal requested");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="withdraw-form">
      {[["amount", "Amount (₹)"], ["account_holder", "Account holder name"], ["account_number", "Bank account number"], ["ifsc", "IFSC code"]].map(([k, l]) => (
        <Input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`withdraw-${k}`} />
      ))}
      <Button className="rounded-full sm:col-span-2" onClick={submit} data-testid="withdraw-submit">Request withdrawal</Button>
    </div>
  );
};

export const MemberEarnings = () => {
  const [d] = useMember("/mlm/me/dashboard");
  const [comm] = useMember("/mlm/me/commissions");
  const [wallet, reload, call] = useMember("/mlm/me/wallet");
  if (!d || !comm || !wallet) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Personal sales" value={`${inr(d.personal_sales.amount)} · ${d.personal_sales.count}`} testid="earn-personal" />
        <Stat label="Team sales" value={`${inr(d.team_sales.amount)} · ${d.team_sales.count}`} testid="earn-team" />
        <Stat label="Eligible commission" value={inr(d.eligible_commission)} testid="earn-eligible" />
        <Stat label="Wallet balance" value={inr(wallet.balance)} testid="earn-wallet" />
      </div>
      <Card title="Commission history" testid="member-commissions">
        <Rows items={comm} empty="No commission yet. Commission is recorded only on verified product sales, under the company's commission rules." render={(c) => (
          <div key={c.id} className="flex flex-wrap justify-between gap-2 py-2"><span>Level {c.level} · {c.percent}% of {inr(c.base_amount)} · {c.source_ref}</span><span className="font-mono-num font-semibold">{inr(c.amount)} <span className="text-xs font-normal capitalize text-slate-500">{c.status} · {fmtDate(c.created_at)}</span></span></div>
        )} />
      </Card>
      <Card title="Wallet ledger" testid="member-ledger">
        <Rows items={wallet.entries} empty="No wallet entries yet." render={(e) => (
          <div key={e.id} className="flex flex-wrap justify-between gap-2 py-2"><span>{e.note}</span><span className={`font-mono-num font-semibold ${e.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{e.amount < 0 ? "−" : "+"}{inr(Math.abs(e.amount))} <span className="text-xs font-normal text-slate-500">{fmtDate(e.created_at)}</span></span></div>
        )} />
      </Card>
      <Card title="Withdrawal request" testid="member-withdrawals">
        <Withdraw wallet={wallet} reload={reload} call={call} />
        <Rows items={wallet.withdrawals} empty="" render={(w) => <div key={w.id} className="flex justify-between py-2"><span>{w.reference}</span><span className="capitalize">{inr(w.amount)} · {w.status}</span></div>} />
      </Card>
    </div>
  );
};
